import React, { useState, useEffect } from 'react';
import { useAccountModules } from '../../services/accountModulesService';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import PlatformDots from './PlatformDots';
import AIAssistant from '../ui/AIAssistant';
import {
  Settings,
  CreditCard,
  LogOut,
  Menu,
  X,
  ChevronDown,
  ChevronsLeft,
  ChevronsRight,
  Shield,
  Sun,
  Moon,
  Bell,
  LifeBuoy,
  ChevronRight,
} from 'lucide-react';
import LanguageSelector from '../ui/LanguageSelector';
import ProjectSwitcher from './ProjectSwitcher';
import SectionTabs from './SectionTabs';
import { SECTION_GROUPS, SectionGroup, SectionTab, TONES, findGroup, isGroupActive, visibleTabs } from './navigation';
import { notificationService } from '../../services/notificationService';
import { whitelabelService, WhitelabelBranding } from '../../services/whitelabelService';

const DashboardLayout: React.FC = () => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  // Barra recolhida (só ícones) no computador; a escolha fica salva neste navegador
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem('sidebar-collapsed') === '1'; } catch { return false; }
  });
  const toggleCollapsed = () => {
    setSidebarCollapsed((prev) => {
      try { localStorage.setItem('sidebar-collapsed', prev ? '0' : '1'); } catch { /* ignore */ }
      return !prev;
    });
  };
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [whitelabel, setWhitelabel] = useState<WhitelabelBranding | null>(null);

  // O tour de boas-vindas abre/fecha a gaveta do menu no celular
  useEffect(() => {
    const onTour = (e: Event) => setSidebarOpen(!!(e as CustomEvent).detail);
    window.addEventListener('tour:sidebar', onTour);
    return () => window.removeEventListener('tour:sidebar', onTour);
  }, []);

  // Update time every minute
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  // Load whitelabel branding
  useEffect(() => {
    const applyBranding = (data: WhitelabelBranding) => {
      setWhitelabel(data);
      if (data.favicon_url) {
        const link = document.querySelector("link[rel*='icon']") as HTMLLinkElement || document.createElement('link');
        link.type = 'image/x-icon';
        link.rel = 'shortcut icon';
        link.href = data.favicon_url;
        document.getElementsByTagName('head')[0].appendChild(link);
      }
      if (data.company_name) {
        document.title = data.company_name;
      }
    };
    const loadWhitelabel = async () => {
      try {
        const host = window.location.host;
        const response = await whitelabelService.checkDomain(host);
        if (response.success && response.data) {
          applyBranding(response.data);
          return;
        }
        // Fallback: nenhum domínio/subdomínio casou (ex.: revendedor acessando o admin
        // compartilhado). Aplica a whitelabel ATIVA do próprio usuário logado, pra a
        // marca aparecer na conta dele mesmo sem domínio custom configurado.
        const own = await whitelabelService.getConfig();
        if (own.success && own.data && own.data.is_active) {
          applyBranding(own.data);
        }
      } catch (e) { /* silent */ }
    };
    loadWhitelabel();
  }, []);

  // Fetch unread notifications count
  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const data = await notificationService.getUnreadCount();
        setUnreadCount(data.count);
      } catch {
        // silently fail
      }
    };
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 60000);
    return () => clearInterval(interval);
  }, []);

  const getGreeting = () => {
    const hour = currentTime.getHours();
    if (hour < 12) return t('greeting.morning', 'Good morning');
    if (hour < 18) return t('greeting.afternoon', 'Good afternoon');
    return t('greeting.evening', 'Good evening');
  };

  const modules = useAccountModules();
  // Barra lateral curta: um item por assunto (navigation.ts). As outras telas do assunto
  // viram abas no topo da página (SectionTabs). Módulos desligados somem do menu.
  const navItems = SECTION_GROUPS.map((group) => {
    const tabs = visibleTabs(group, modules);
    return tabs.length ? { group, tabs, href: tabs[0].href } : null;
  }).filter(Boolean) as { group: SectionGroup; tabs: SectionTab[]; href: string }[];
  const activeGroup = findGroup(location.pathname, modules);

  // Get page title based on current route (o assunto do menu; a tela tem o título dela)
  const getPageTitle = () => {
    const path = location.pathname;
    if (activeGroup) return t(activeGroup.key, activeGroup.fallback);
    if (path.startsWith('/audiences')) return t('nav.audiences', 'Rastreamento do site');
    if (path.startsWith('/notifications')) return t('notifications.title', 'Notifications');
    if (path.startsWith('/admin')) return t('nav.admin');
    return brandName;
  };

  const brandName = whitelabel?.company_name || 'DevSkin Marketing';

  const renderNavItem = (item: { group: SectionGroup; tabs: SectionTab[]; href: string }) => {
    const { group, tabs, href } = item;
    const Icon = group.icon;
    const active = activeGroup?.id === group.id || isGroupActive(tabs, location.pathname);
    const label = t(group.key, group.fallback);
    const tone = TONES[group.tone || 'slate'];
    // Itens do assunto: ícone colorido + nome + uma linha curta. Conta (rodapé) fica compacta.
    const rich = !!group.desc && !group.account;
    const desc = group.desc ? t(`nav.sectionDesc.${group.id}`, group.desc) : '';
    return (
      <Link
        key={group.id}
        to={href}
        data-tour={group.tour}
        title={sidebarCollapsed ? label : undefined}
        aria-current={active ? 'page' : undefined}
        className={`group relative flex items-center gap-3 rounded-xl transition-all duration-200 ${rich ? 'px-2.5 py-2' : 'px-2.5 py-1.5'} ${sidebarCollapsed ? 'lg:justify-center lg:px-0 lg:w-12 lg:h-12 lg:mx-auto' : ''} ${
          active
            ? 'bg-white/[0.07] ring-1 ring-white/10 text-white'
            : group.highlight
              ? 'text-white bg-emerald-500/[0.07] ring-1 ring-emerald-400/25 hover:bg-emerald-500/[0.12]'
              : 'text-gray-400 hover:text-white hover:bg-white/[0.04]'
        }`}
        onClick={() => setSidebarOpen(false)}
      >
        {active && (
          <span className={`absolute -left-3 top-1/2 -translate-y-1/2 w-1 h-8 rounded-r-full bg-gradient-to-b ${tone.bar} ${sidebarCollapsed ? 'lg:-left-2' : ''}`} />
        )}
        <span className={`flex items-center justify-center rounded-xl shrink-0 transition-all duration-200 ${rich ? 'w-9 h-9' : 'w-8 h-8'} ${
          active ? tone.active : `${tone.idle} group-hover:scale-105`
        }`}>
          <Icon className={rich ? 'w-[18px] h-[18px]' : 'w-4 h-4'} />
        </span>
        <span className={`flex-1 min-w-0 ${sidebarCollapsed ? 'lg:hidden' : ''}`}>
          <span className={`flex items-center gap-1.5 ${rich ? 'text-[14px] font-semibold' : 'text-sm font-medium'}`}>
            <span className="truncate">{label}</span>
            {group.highlight && (
              <span className="shrink-0 rounded-md bg-emerald-500/20 px-1.5 py-px text-[10px] font-bold uppercase tracking-wide text-emerald-300">Google</span>
            )}
          </span>
          {rich && (
            <span className={`block truncate text-[11.5px] leading-tight mt-0.5 ${active ? 'text-gray-300' : 'text-gray-500 group-hover:text-gray-400'}`}>{desc}</span>
          )}
        </span>
        {rich && (
          <ChevronRight className={`w-4 h-4 shrink-0 transition-all ${sidebarCollapsed ? 'lg:hidden' : ''} ${active ? 'text-gray-300' : 'text-transparent group-hover:text-gray-500 -translate-x-1 group-hover:translate-x-0'}`} />
        )}
      </Link>
    );
  };
  const brandLogo = whitelabel?.logo_url || 'https://devskin.com/images/devskin_white.png';

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-gray-50 to-gray-100 dark:from-gray-900 dark:via-gray-900 dark:to-gray-950">
      {/* Mobile Sidebar Backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm z-40 lg:hidden transition-opacity"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-72 ${sidebarCollapsed ? 'lg:w-20' : 'lg:w-72'} bg-gradient-to-b from-gray-900 via-gray-900 to-gray-950 transform transition-all duration-300 ease-out lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Decorative gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary-600/5 via-transparent to-purple-600/5 pointer-events-none" />

        <div className="relative flex flex-col h-full overflow-hidden">
          {/* Logo Section - Fixed at top */}
          <div className={`flex-shrink-0 flex items-center justify-between h-16 sm:h-20 px-5 ${sidebarCollapsed ? 'lg:justify-center lg:px-2' : ''}`}>
            <Link to="/dashboard" className="flex items-center gap-3 group min-w-0" onClick={() => setSidebarOpen(false)}>
              <div className="relative shrink-0">
                <div className="absolute inset-0 bg-gradient-to-br from-primary-500 to-purple-600 rounded-xl blur-lg opacity-30 group-hover:opacity-50 transition-opacity" />
                <img
                  src={brandLogo}
                  alt={brandName}
                  className="relative w-10 h-10 object-contain"
                />
              </div>
              <span className={`font-bold text-white text-lg tracking-tight truncate ${sidebarCollapsed ? 'lg:hidden' : ''}`}>{brandName}</span>
            </Link>
            <button
              className="lg:hidden p-2 rounded-xl hover:bg-white/10 text-gray-400 hover:text-white transition-colors"
              onClick={() => setSidebarOpen(false)}
              aria-label={t('common.close', 'Fechar')}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Project Switcher (escondido na barra recolhida: abra a barra para trocar de projeto) */}
          <div className={`flex-shrink-0 px-4 mb-2 ${sidebarCollapsed ? 'lg:hidden' : ''}`}>
            <ProjectSwitcher />
          </div>

          {/* Navigation (rola sozinha se a tela for baixa) */}
          <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-dark pb-2">
            <div className={`px-6 pt-1 pb-2 ${sidebarCollapsed ? 'lg:hidden' : ''}`}>
              <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                {t('nav.menu', 'Menu')}
              </p>
            </div>
            <nav data-tour="sidebar" className={`px-3 space-y-1 ${sidebarCollapsed ? 'lg:px-2' : ''}`}>
              {navItems.filter((item) => !item.group.account).map(renderNavItem)}
            </nav>
          </div>

          {/* Account section - fixed at bottom */}
          <div className="flex-shrink-0 border-t border-gray-800/60">
            <div className={`px-6 pt-3 pb-1.5 ${sidebarCollapsed ? 'lg:hidden' : ''}`}>
              <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                {t('nav.account', 'Conta')}
              </p>
            </div>
            <div className={`px-3 space-y-1 pb-2 ${sidebarCollapsed ? 'lg:px-2 lg:pt-2' : ''}`}>
              {navItems.filter((item) => item.group.account).map(renderNavItem)}

              {/* Admin Link */}
              {user?.role && ['admin', 'super_admin'].includes(user.role) && (
                <Link
                  to="/admin"
                  title={sidebarCollapsed ? t('nav.admin') : undefined}
                  className={`group relative flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${sidebarCollapsed ? 'lg:justify-center lg:px-0 lg:w-12 lg:h-12 lg:mx-auto' : ''} ${
                    location.pathname.startsWith('/admin')
                      ? 'bg-gradient-to-r from-amber-600/20 to-orange-600/20 text-amber-400'
                      : 'text-gray-400 hover:text-white hover:bg-white/5'
                  }`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <span className="p-1.5 rounded-lg bg-gray-800/50 text-gray-500 group-hover:bg-gray-800 group-hover:text-gray-300 shrink-0">
                    <Shield className="w-4 h-4" />
                  </span>
                  <span className={`truncate ${sidebarCollapsed ? 'lg:hidden' : ''}`}>{t('nav.admin')}</span>
                </Link>
              )}

              {/* Recolher / abrir a barra (só no computador) */}
              <button
                onClick={toggleCollapsed}
                title={sidebarCollapsed ? t('nav.sections.expand', 'Abrir menu') : t('nav.sections.collapse', 'Recolher menu')}
                className={`hidden lg:flex w-full items-center rounded-xl text-sm text-gray-500 hover:text-gray-300 hover:bg-white/5 transition-colors ${
                  sidebarCollapsed ? 'justify-center h-10' : 'gap-3 px-3 py-2'
                }`}
              >
                <span className="p-1.5">
                  {sidebarCollapsed ? <ChevronsRight className="h-4 w-4" /> : <ChevronsLeft className="h-4 w-4" />}
                </span>
                {!sidebarCollapsed && <span>{t('nav.sections.collapse', 'Recolher menu')}</span>}
              </button>
            </div>

            {/* User Profile Card */}
            <div className={`px-4 pb-4 ${sidebarCollapsed ? 'lg:px-2' : ''}`}>
              <div className={`p-3 rounded-2xl bg-gradient-to-br from-gray-800/80 to-gray-800/40 border border-gray-700/50 backdrop-blur-sm ${sidebarCollapsed ? 'lg:p-2' : ''}`}>
                <div className={`flex items-center gap-3 ${sidebarCollapsed ? 'lg:justify-center' : ''}`}>
                  <div className="relative shrink-0" title={sidebarCollapsed ? `${user?.first_name || ''} ${user?.last_name || ''}` : undefined}>
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500 to-purple-600 flex items-center justify-center shadow-lg">
                      <span className="text-sm font-bold text-white">
                        {user?.first_name?.[0]}{user?.last_name?.[0]}
                      </span>
                    </div>
                    <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-gray-900" />
                  </div>
                  <div className={`flex-1 min-w-0 ${sidebarCollapsed ? 'lg:hidden' : ''}`}>
                    <p className="text-sm font-semibold text-white truncate">
                      {user?.first_name} {user?.last_name}
                    </p>
                    <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                  </div>
                  <button
                    onClick={logout}
                    className={`p-2 rounded-lg text-gray-500 hover:text-red-400 hover:bg-red-500/10 transition-colors ${sidebarCollapsed ? 'lg:hidden' : ''}`}
                    title={t('nav.logout')}
                    aria-label={t('nav.logout')}
                  >
                    <LogOut className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className={sidebarCollapsed ? 'lg:pl-20' : 'lg:pl-72'}>
        {/* Top Header */}
        <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 dark:bg-gray-900/80 border-b border-gray-200/50 dark:border-gray-800/50">
          <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
            {/* Left Section */}
            <div className="flex items-center gap-4">
              <button
                className="lg:hidden p-2.5 rounded-xl bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                onClick={() => setSidebarOpen(true)}
              >
                <Menu className="w-5 h-5 text-gray-600 dark:text-gray-300" />
              </button>

              {/* Page Title & Breadcrumb */}
              <div className="hidden sm:block">
                <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {getPageTitle()}
                </h1>
              </div>
            </div>

            {/* Right Section */}
            <div className="flex items-center gap-1 sm:gap-2 md:gap-3">
              {/* Platform Switcher */}
              <PlatformDots current="marketing" />

              {/* Theme Toggle */}
              <button
                onClick={toggleTheme}
                className="relative p-2 sm:p-2.5 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200 group"
                title={isDark ? t('settings.lightMode') : t('settings.darkMode')}
              >
                <div className="relative">
                  {isDark ? (
                    <Sun className="w-4 h-4 sm:w-5 sm:h-5 text-amber-500 group-hover:rotate-45 transition-transform duration-300" />
                  ) : (
                    <Moon className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 group-hover:-rotate-12 transition-transform duration-300" />
                  )}
                </div>
              </button>

              {/* Language Selector - hidden on very small screens */}
              <div className="hidden sm:block">
                <LanguageSelector />
              </div>

              {/* Help */}
              <a
                href="https://crm.devskin.com/contact"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 sm:p-2.5 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                <LifeBuoy className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
              </a>

              {/* Notifications */}
              <button
                onClick={() => navigate('/notifications')}
                className="relative p-2 sm:p-2.5 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                <Bell className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 dark:text-gray-400" />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] sm:min-w-[18px] sm:h-[18px] flex items-center justify-center bg-red-500 text-white text-[9px] sm:text-[10px] font-bold rounded-full ring-2 ring-white dark:ring-gray-900 px-0.5 sm:px-1">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </button>

              {/* Divider - hidden on mobile */}
              <div className="hidden sm:block w-px h-6 bg-gray-200 dark:bg-gray-700" />

              {/* User Menu */}
              <div className="relative">
                <button
                  className="flex items-center gap-1.5 sm:gap-3 p-1 sm:p-1.5 sm:pr-3 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                >
                  <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-xl bg-gradient-to-br from-primary-500 to-purple-600 flex items-center justify-center shadow-md">
                    <span className="text-xs sm:text-sm font-bold text-white">
                      {user?.first_name?.[0]}{user?.last_name?.[0]}
                    </span>
                  </div>
                  <div className="hidden md:block text-left">
                    <p className="text-sm font-medium text-gray-900 dark:text-white leading-tight">
                      {user?.first_name}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 leading-tight">
                      {getGreeting()}
                    </p>
                  </div>
                  <ChevronDown className="hidden sm:block w-4 h-4 text-gray-400" />
                </button>

                {userMenuOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setUserMenuOpen(false)}
                    />
                    <div className="absolute right-0 mt-2 w-[calc(100vw-2rem)] sm:w-64 bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 py-2 z-50 overflow-hidden">
                      {/* User Info Header */}
                      <div className="px-4 py-3 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 border-b border-gray-100 dark:border-gray-700">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-500 to-purple-600 flex items-center justify-center shadow-lg">
                            <span className="text-lg font-bold text-white">
                              {user?.first_name?.[0]}{user?.last_name?.[0]}
                            </span>
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900 dark:text-white">
                              {user?.first_name} {user?.last_name}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">{user?.email}</p>
                          </div>
                        </div>
                      </div>

                      {/* Menu Items */}
                      <div className="py-2">
                        <Link
                          to="/settings"
                          className="flex items-center gap-3 px-4 py-2.5 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                          onClick={() => setUserMenuOpen(false)}
                        >
                          <Settings className="w-4 h-4 text-gray-400" />
                          <span className="text-sm">{t('nav.settings')}</span>
                        </Link>
                        <Link
                          to="/subscription"
                          className="flex items-center gap-3 px-4 py-2.5 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                          onClick={() => setUserMenuOpen(false)}
                        >
                          <CreditCard className="w-4 h-4 text-gray-400" />
                          <span className="text-sm">{t('nav.subscription')}</span>
                        </Link>
                      </div>

                      {/* Logout */}
                      <div className="border-t border-gray-100 dark:border-gray-700 pt-2">
                        <button
                          className="flex items-center gap-3 w-full px-4 py-2.5 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                          onClick={() => {
                            setUserMenuOpen(false);
                            logout();
                          }}
                        >
                          <LogOut className="w-4 h-4" />
                          <span className="text-sm font-medium">{t('nav.logout')}</span>
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 sm:p-6 lg:p-8">
          <SectionTabs />
          <Outlet />
        </main>
      </div>
      <AIAssistant />
    </div>
  );
};

export default DashboardLayout;
